// API client for the matrix-llm-bot workspaces proxy (read-only tasks API).
//
// All requests go to `${homeserverUrl}/${bridgeProvider}/workspaces/:id/tasks*`
// with the user's Matrix access token as a Bearer token. The bot validates the
// token via the homeserver `whoami` endpoint and forwards to the coding-agent
// manager.
//
// `bridgeProvider` is taken verbatim from room state and already includes the
// `-bridge` segment, so we never append it here.
//
// Auth and transparent token refresh are shared with the bridge-explorer
// window: the refresh SDK channels are handled generically by the main window
// for any source window, so we reuse those modules rather than duplicate them.

import { trimLeadingSlash, trimTrailingSlash } from '../../utils/common';
import { getToken, refreshToken } from '../bridge-explorer/tokenRefresh';
import type { TaskDetail, TaskListResponse, TaskStats, TaskSummary } from './types';

export type TaskAuth = {
  token: string;
  refresh: () => Promise<string>;
};

/** A typed error carrying the HTTP status for inline error rendering. */
export class TaskApiError extends Error {
  status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = 'TaskApiError';
    this.status = status;
  }
}

/** Build the workspaces base URL: `${homeserverUrl}/${bridgeProvider}/workspaces`. */
export function getTaskBoardBaseUrl(homeserverUrl: string, bridgeProvider: string): string {
  return `${trimTrailingSlash(homeserverUrl)}/${trimLeadingSlash(
    trimTrailingSlash(bridgeProvider),
  )}/workspaces`;
}

async function extractErrorMessage(res: Response): Promise<string> {
  try {
    const data = await res.clone().json();
    if (data && typeof data === 'object' && typeof data.error === 'string') {
      return data.error;
    }
  } catch {
    // ignore JSON parse errors and fall back to status text
  }
  return res.statusText || `HTTP ${res.status}`;
}

async function rawFetch(url: string, token: string): Promise<Response> {
  try {
    return await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  } catch (e) {
    throw new TaskApiError(e instanceof Error ? e.message : 'Network error', 0);
  }
}

async function authedFetchWith(
  url: string,
  token: string,
  refresh: () => Promise<string>,
): Promise<Response> {
  let res = await rawFetch(url, token);
  if (res.status === 401) {
    try {
      const freshToken = await refresh();
      res = await rawFetch(url, freshToken);
    } catch {
      // Refresh failed; fall through and surface the original 401 below.
    }
  }
  if (!res.ok) {
    throw new TaskApiError(await extractErrorMessage(res), res.status);
  }
  return res;
}

/**
 * Fetch with the current Matrix access token. On a 401 (token expired), ask the
 * main window for a fresh token and retry once, so refresh is transparent.
 */
async function authedFetch(url: string): Promise<Response> {
  return authedFetchWith(url, getToken(), refreshToken);
}

const tasksApiBase = (baseUrl: string, workspaceId: string): string =>
  `${baseUrl}/${encodeURIComponent(workspaceId)}/tasks`;

/**
 * GET `.../tasks/stats` — counts aggregated by status.
 *
 * When `auth` is provided (e.g. the main-window side panel, which holds the
 * live Matrix token directly), it supplies the Bearer token and refresh
 * callback. Otherwise the shared token cache is used with transparent refresh
 * (the board window path).
 */
export async function fetchWorkspaceTaskStats(
  baseUrl: string,
  workspaceId: string,
  auth?: TaskAuth,
): Promise<TaskStats> {
  const url = `${tasksApiBase(baseUrl, workspaceId)}/stats`;
  if (auth) {
    const res = await authedFetchWith(url, auth.token, auth.refresh);
    return res.json();
  }
  const res = await authedFetch(url);
  return res.json();
}

/** GET `.../tasks` — the full task list. */
export async function fetchWorkspaceTasks(
  baseUrl: string,
  workspaceId: string,
): Promise<TaskSummary[]> {
  const res = await authedFetch(tasksApiBase(baseUrl, workspaceId));
  const data = (await res.json()) as TaskListResponse;
  return data.tasks ?? [];
}

/** GET `.../tasks/detail?slug=<slug>` — task metadata + inline preset docs. */
export async function fetchWorkspaceTaskDetail(
  baseUrl: string,
  workspaceId: string,
  slug: string,
): Promise<TaskDetail> {
  const url = `${tasksApiBase(baseUrl, workspaceId)}/detail?slug=${encodeURIComponent(slug)}`;
  const res = await authedFetch(url);
  return res.json();
}
